import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Hero from "@/components/home/Hero";
import FlagshipSpotlight from "@/components/home/FlagshipSpotlight";
import Marquee from "@/components/Marquee";
import PtfButton from "@/components/PtfButton";
import PartnershipForm from "@/components/PartnershipForm";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { api } from "@/lib/api";
import {
  PILLARS, TOURISM_PRINCIPLES, APPROACH, IMAGES, locationLabel,
} from "@/lib/content";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-6 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

function WhoWeAre() {
  const { t } = useTranslation();
  return (
    <section data-testid="who-we-are" className="bg-ptf-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>{t("home.who_eyebrow")}</Eyebrow></Reveal>
          <MaskText
            lines={t("home.who_headline", { returnObjects: true })}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tight text-ptf-primary"
          />
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <p className="text-lg leading-relaxed text-ptf-text/80">
              {t("home.who_p1")}
            </p>
            <p className="mt-6 leading-relaxed text-ptf-text/70">
              {t("home.who_p2")}
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.16em] text-ptf-primary hover:text-ptf-secondary transition-colors">
              {t("home.who_cta")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AreasOfWork() {
  const { t } = useTranslation();
  return (
    <section data-testid="areas-of-work" className="bg-ptf-primary text-ptf-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <Reveal><Eyebrow light>{t("home.areas_eyebrow")}</Eyebrow></Reveal>
            <MaskText
              lines={t("home.areas_headline", { returnObjects: true })}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tight"
              lastLineClassName="text-ptf-accent"
            />
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-ptf-bg/70 leading-relaxed">
              {t("home.areas_sub")}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ptf-bg/10 border border-ptf-bg/10">
          {PILLARS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.06} className="h-full">
              <Link
                to={`/focus#${p.slug}`}
                data-testid={`pillar-${p.slug}`}
                className="group relative flex h-full flex-col justify-between bg-ptf-primary p-7 lg:p-8 min-h-[280px] overflow-hidden transition-colors duration-500 hover:bg-ptf-secondary"
              >
                <img src={p.image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-20 transition-opacity duration-700 ease-out" loading="lazy" />
                <div className="relative z-10 flex items-start justify-between">
                  <span className="font-display text-2xl text-ptf-accent">{p.num}</span>
                  <ArrowUpRight className="h-5 w-5 text-ptf-bg/40 transition-all duration-300 group-hover:text-ptf-accent group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
                <div className="relative z-10 mt-8">
                  <h3 className="font-display uppercase text-2xl lg:text-[1.6rem] leading-none tracking-tight">{p.title}</h3>
                  <p className="mt-4 text-sm text-ptf-bg/65 leading-relaxed">{p.short}</p>
                </div>
                <span className="relative z-10 mt-6 h-0.5 w-10 bg-ptf-accent transition-all duration-500 group-hover:w-full" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BigIdea() {
  const { t } = useTranslation();
  return (
    <section data-testid="big-idea" className="relative bg-ptf-text text-ptf-bg overflow-hidden grain">
      <img src={IMAGES.bigIdea} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-ptf-primary/90 via-ptf-primary/60 to-transparent" />
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 py-24 md:py-36">
        <MaskText
          lines={t("home.bigidea_headline", { returnObjects: true })}
          className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl"
        />
        <Reveal delay={0.2}>
          <p className="mt-10 max-w-xl text-lg text-ptf-bg/80 leading-relaxed">
            <Trans i18nKey="home.bigidea_p" components={{ link1: <Link to="/flagship" className="underline underline-offset-4 hover:text-ptf-accent" /> }}>
              This approach was first tested in the villages of Uttarakhand, where
              travel income brought solar power, bee farming and new reasons to stay.
              It now reaches the Serengeti through our <link1>flagship expedition</link1>,
              and it will go wherever a community invites it.
            </Trans>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ResponsibleTourism() {
  const { t } = useTranslation();
  return (
    <section data-testid="responsible-tourism" className="bg-ptf-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal><Eyebrow>{t("home.tourism_eyebrow")}</Eyebrow></Reveal>
            <MaskText
              lines={t("home.tourism_headline", { returnObjects: true })}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
            />
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-lg text-lg leading-relaxed text-ptf-text/75">
                <Trans i18nKey="home.tourism_p" components={{ link1: <Link to="/focus#responsible-tourism" className="underline underline-offset-4 hover:text-ptf-primary" /> }}>
                  Travel in India can fund conservation, sustain living heritage and keep
                  income in the villages that host it. Our <link1>responsible tourism work</link1>{" "}
                  is built on that premise.
                </Trans>
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={IMAGES.tourism} alt="Two trekkers rest among the rocks of the Ladhiya Valley, from Planet Trotter field photography" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-105" loading="lazy" />
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TOURISM_PRINCIPLES.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.08}>
              <div className="group h-full border-t-2 border-ptf-primary/15 pt-6 transition-colors duration-300 hover:border-ptf-accent">
                <span className="font-display text-xl text-ptf-primary/40">{p.num}</span>
                <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mt-2 leading-none">{p.title}</h3>
                <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function OurApproach() {
  const { t } = useTranslation();
  return (
    <section data-testid="approach" className="bg-ptf-secondary text-ptf-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="mb-16 max-w-3xl">
          <Reveal><Eyebrow light>{t("home.approach_eyebrow")}</Eyebrow></Reveal>
          <MaskText
            lines={t("home.approach_headline", { returnObjects: true })}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-ptf-bg/70 leading-relaxed">
              {t("home.approach_p")}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-4">
          {APPROACH.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.06}>
              <div className="relative">
                <span className="font-display text-5xl text-ptf-accent">{s.num}</span>
                <div className="mt-4 h-px w-full bg-ptf-bg/20" />
                <h3 className="font-display uppercase text-xl tracking-tight mt-4">{s.title}</h3>
                <p className="mt-2 text-sm text-ptf-bg/65 leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedProgrammes() {
  const { data: programmes = [] } = useQuery({
    queryKey: ["programmes"],
    queryFn: async () => (await api.get("/programmes")).data,
  });
  const featured = programmes.filter((p) => p.featured).slice(0, 8);
  if (featured.length === 0) return null;

  return (
    <section data-testid="featured-programmes" className="bg-ptf-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <Reveal><Eyebrow>Our work</Eyebrow></Reveal>
            <MaskText
              lines={["Programmes we're", "building."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tight text-ptf-primary"
              lastLineClassName="text-ptf-secondary"
            />
          </div>
          <Reveal delay={0.1}>
            <PtfButton to="/programmes" variant="outline">View All Programmes</PtfButton>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06} className="h-full">
              <Link to={`/programmes/${p.slug || p.id}`} data-testid={`featured-programme-${p.id}`} className="group h-full flex flex-col border border-ptf-primary/12 overflow-hidden hover:border-ptf-primary/30 transition-colors">
                {p.images?.[0] && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-[0.58rem] uppercase tracking-[0.14em] px-2 py-1 bg-ptf-accent/20 text-ptf-primary">{p.status}</span>
                    <span className="text-[0.58rem] uppercase tracking-[0.14em] text-ptf-text/50">{p.category}</span>
                  </div>
                  <h3 className="font-display uppercase text-xl tracking-tight text-ptf-primary leading-tight group-hover:text-ptf-secondary transition-colors">{p.name}</h3>
                  {(p.short_description || p.objective) && <div className="mt-3 flex-1"><p className="text-sm text-ptf-text/70 leading-relaxed line-clamp-3">{p.short_description || p.objective}</p></div>}
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ptf-primary">View Programme <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnershipsHome() {
  const { t } = useTranslation();
  return (
    <section data-testid="partnerships-home" className="bg-ptf-primary text-ptf-bg py-24 md:py-36 grain relative">
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow light>{t("home.partnerships_eyebrow")}</Eyebrow></Reveal>
          <MaskText
            lines={t("home.partnerships_headline", { returnObjects: true })}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-ptf-bg/75 leading-relaxed">
              {t("home.partnerships_p")}
            </p>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-2">
            {["Government", "CSR", "NGOs", "Universities", "Research", "Foundations", "International", "Communities"].map((tag) => (
              <span key={tag} className="text-[0.7rem] uppercase tracking-[0.14em] border border-ptf-bg/25 px-3 py-1.5 text-ptf-bg/70">{tag}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="border border-ptf-bg/15 p-7 md:p-10">
              <h3 className="font-display uppercase text-2xl tracking-tight mb-8">{t("home.partnerships_form_heading")}</h3>
              <PartnershipForm dark />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ImpactHome() {
  const { t } = useTranslation();
  return (
    <section data-testid="impact-home" className="bg-ptf-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>{t("home.impact_eyebrow")}</Eyebrow></Reveal>
          <MaskText
            lines={t("home.impact_headline", { returnObjects: true })}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-ptf-text/75 leading-relaxed max-w-xl">
              {t("home.impact_p")}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-8">
              <PtfButton to="/impact" variant="outline" data-testid="impact-home-cta">{t("home.impact_cta")}</PtfButton>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src="/dawn/vor-computers.jpg" alt="Village children at computers for the first time in nine years" className="h-full w-full object-cover" loading="lazy" />
              <p className="absolute bottom-0 inset-x-0 bg-ptf-primary/85 text-ptf-bg text-xs px-4 py-3">
                {t("home.impact_caption")}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useTranslation();
  return (
    <section data-testid="final-cta" className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 text-center">
        <MaskText
          lines={t("home.final_headline", { returnObjects: true })}
          className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight"
          lastLineClassName="text-ptf-accent"
        />
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl mx-auto text-lg text-ptf-bg/75 leading-relaxed">
            {t("home.final_p")}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <PtfButton to="/partnerships" variant="accent">{t("home.final_partner")}</PtfButton>
            <PtfButton to="/contact" variant="outlineLight">{t("home.final_contact")}</PtfButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        path="/"
        description="Planet Trotter Foundation is an environmental foundation in India that brings conservation, responsible travel and community development together as one body of work."
      />
      <Hero />
      <WhoWeAre />
      <FlagshipSpotlight />
      <AreasOfWork />
      <FeaturedProgrammes />
      <BigIdea />
      <div className="bg-ptf-bg text-ptf-primary py-8 md:py-12 border-y border-ptf-primary/10">
        <Marquee items={["Conservation", "Communities", "Responsible Exploration", "Education", "Livelihoods", "Sustainability"]} />
      </div>
      <ResponsibleTourism />
      <OurApproach />
      <PartnershipsHome />
      <ImpactHome />
      <FinalCta />
    </>
  );
}
